import type { ISettingValueMaster } from "@bbj-types/setting";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { AutocompleteProps, AutocompleteRenderGroupParams, TextFieldProps } from "@mui/material";
import {
  Checkbox,
  Collapse,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  createFilterOptions,
} from "@mui/material";
import { CheckboxStatus } from "@root/utils/enums/checkbox";
import React, { useCallback, useState } from "react";
import { AutocompleteSettings } from "../AutocompleteSettings";

export enum SortType {
  ASC = "asc",
  DESC = "desc",
}

export interface IAutocompleteCheckboxesProps<
  T extends Pick<ISettingValueMaster, "label" | "value" | "group">,
  DisableClearable extends boolean | undefined = undefined,
> extends Omit<
    AutocompleteProps<T, true, DisableClearable, false>,
    | "multiple"
    | "getOptionLabel"
    | "disableCloseOnSelect"
    | "renderGroup"
    | "renderOption"
    | "renderInput"
    | "inputValue"
    | "onInputChange"
    | "renderValue"
    | "limitTags"
  > {
  value: Required<AutocompleteProps<T, true, DisableClearable, false>>["value"];
  onChange: Required<AutocompleteProps<T, true, DisableClearable, false>>["onChange"];
  sortGroupBy?: SortType;
  TextFieldProps?: TextFieldProps;
}

export const AutocompleteCheckboxes = <
  T extends Pick<ISettingValueMaster, "label" | "value" | "group">,
  DisableClearable extends boolean | undefined,
>({
  value,
  options,
  onChange,
  TextFieldProps,
  sortGroupBy = SortType.ASC,
  groupBy: propsGroupBy,
  ...props
}: IAutocompleteCheckboxesProps<T, DisableClearable>) => {
  const groupBy = propsGroupBy ?? ((option: T) => option.group ?? "");
  const [inputValue, setInputValue] = useState("");
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(
    Object.fromEntries(options.map((option) => [groupBy(option) ?? "", true]))
  );
  const [filteredOptions, setFilteredOptions] = useState<T[]>([...options]);
  const filter = createFilterOptions<T>();

  const sortByGroup = (a: T, b: T) => {
    const groupA = groupBy(a) ?? "";
    const groupB = groupBy(b) ?? "";

    return sortGroupBy === SortType.ASC ? groupA.localeCompare(groupB) : groupB.localeCompare(groupA);
  };

  const getCheckboxStatus = useCallback(
    (group: ISettingValueMaster["group"]) => {
      if (group === "Sélectionner tout") {
        return value.length > 0
          ? value.length < options.length
            ? CheckboxStatus.INDETERMINATE
            : CheckboxStatus.CHECKED
          : CheckboxStatus.UNCHECKED;
      }
      let status = CheckboxStatus.NONE;
      for (const option of options) {
        if (groupBy(option) === group) {
          if (value.some((v) => option.value === v.value)) {
            status |= CheckboxStatus.CHECKED;
          } else {
            status |= CheckboxStatus.UNCHECKED;
          }
        }
      }
      return status;
    },
    [value, groupBy, options]
  );

  const toggleGroup = useCallback(
    (group: AutocompleteRenderGroupParams["group"], event: React.SyntheticEvent, isSelected: CheckboxStatus) => {
      if (isSelected === CheckboxStatus.CHECKED) {
        const newSelectedOptions = value.filter((option) => groupBy(option) !== group);
        onChange(event, newSelectedOptions, "removeOption");
      } else {
        const newSelectedOptions = [
          ...value,
          ...filteredOptions.filter((option) => groupBy(option) === group && !value.includes(option)),
        ];
        onChange(event, newSelectedOptions, "selectOption");
      }
    },
    [filteredOptions, onChange, value, groupBy]
  );

  return (
    <AutocompleteSettings
      {...props}
      multiple
      options={options}
      disableCloseOnSelect
      slotProps={{
        ...props.slotProps,
        popper: {
          ...props.slotProps?.popper,
          sx: {
            width: "fit-content !important",
            minWidth: "auto",
          },
          placement: "top-start",
        },
        paper: {
          ...props.slotProps?.paper,
          sx: {
            width: "max-content",
          },
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          {...TextFieldProps}
          size="small"
          onKeyDown={(event) => {
            if (event.key === "Backspace" && inputValue.length === 0) {
              event.stopPropagation();
              event.preventDefault();
            }
          }}
        />
      )}
      groupBy={groupBy}
      getOptionLabel={(option) => option.label}
      value={value}
      onChange={onChange}
      inputValue={inputValue}
      onInputChange={(_, inputValue) => {
        setInputValue(inputValue);
        setFilteredOptions(filter([...options], { inputValue, getOptionLabel: (option) => option.label }));
      }}
      filterOptions={(opts, params) => {
        const filtered = filter(opts, params);
        return [
          { label: "Sélectionner Tout", value: "selectAll", group: "Sélectionner tout" } as unknown as T,
          ...filtered.sort(sortByGroup),
        ] as T[];
      }}
      renderValue={(value, getTagProps) => (
        <Typography variant="body1" {...getTagProps({ index: value.length })}>
          {value.length} sélectionné{value.length > 1 ? "s" : ""}
        </Typography>
      )}
      renderOption={(optionprops, option, { selected }) => (
        <MenuItem
          {...optionprops}
          sx={{
            backgroundColor: "transparent !important",
            "&.Mui-selected": {
              backgroundColor: "transparent !important",
            },
            "&:hover": {
              backgroundColor: "transparent !important",
            },
            paddingY: "2px !important",
          }}
          key={option.value}
        >
          <FormControlLabel
            sx={{
              paddingLeft: groupBy(option) ? 2 : 0,
            }}
            checked={selected}
            onClick={(event) => event.preventDefault()}
            control={<Checkbox size="xs" />}
            label={<Typography>{option.label}</Typography>}
          />
        </MenuItem>
      )}
      renderGroup={(params) => {
        const status = getCheckboxStatus(params.group);
        return (
          <React.Fragment key={params.key}>
            {params.group && (
              <MenuItem
                sx={{
                  backgroundColor: "transparent !important",
                  "&.Mui-selected": {
                    backgroundColor: "transparent !important",
                  },
                  "&:hover": {
                    backgroundColor: "transparent !important",
                  },
                  paddingY: "2px !important",
                }}
              >
                <FormControlLabel
                  checked={status === CheckboxStatus.CHECKED}
                  sx={{
                    width: "100%",
                  }}
                  onClick={(event) => {
                    if (params.group === "Sélectionner tout") {
                      if (value.length) {
                        onChange(event, [], "removeOption");
                      } else {
                        onChange(event, [...filteredOptions], "selectOption");
                      }
                    } else {
                      toggleGroup(params.group, event, status);
                    }
                    event.preventDefault();
                  }}
                  control={<Checkbox size="xs" indeterminate={status === CheckboxStatus.INDETERMINATE} />}
                  label={<Typography variant="body1SemiBold">{params.group}</Typography>}
                />
                {params.group !== "Sélectionner tout" && (
                  <IconButton
                    size="small"
                    onClick={() => setOpenGroups((prev) => ({ ...prev, [params.group]: !prev[params.group] }))}
                  >
                    {openGroups[params.group] ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )}
                  </IconButton>
                )}
              </MenuItem>
            )}
            <Collapse in={openGroups[params.group]}>{params.group !== "Sélectionner tout" && params.children}</Collapse>
          </React.Fragment>
        );
      }}
    />
  );
};
